<template>
  <BaseModal
    v-model="isVisible"
    title="Sign In/Sign Up for free to view all content!"
    :max-width="800"
    :show-close-button="true"
    @close="handleClose"
    class="view-limit-modal-wrapper"
    card-class="login-required-modal"
    :z-index="10000000000"
  >
    <div class="modal-content">
      <div class="illustration-container">
        <img
          src="/images/signup-popup.svg"
          alt="Sign up illustration"
          class="illustration"
        />
      </div>

      <h3 class="main-title">
        You have <span class="remaining-count">{{ limitResourceLeft }}</span
        >/3 resource views remaining
      </h3>

      <p class="description">
        Please Sign In or Sign Up for free to view all locked lessons!
      </p>

      <div class="button-container">
        <BaseButton
          variant="primary"
          class="action-btn login-btn"
          @click="navigateToLogin"
        >
          Sign In
        </BaseButton>

        <v-btn
          variant="flat"
          color="primary"
          size="large"
          class="action-btn signup-btn"
          @click="navigateToSignup"
        >
          Sign Up
        </v-btn>
      </div>
    </div>
  </BaseModal>

  <!-- Auth Modal -->
  <AuthModal
    :is-open="showAuthModal"
    :initial-view="authView"
    @close="handleCloseAuthModal"
    @login-success="handleLoginSuccess"
    :z-index="100000000"
  />
</template>

<script setup>
import { computed, defineProps, defineEmits, ref, watch } from "vue";
import { useStore } from "vuex";
import BaseModal from "@/components/modal/BaseModal.vue";
import AuthModal from "@/components/modal/AuthModal.vue";
import BaseButton from "../common/BaseButton.vue";

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: true,
  },
  title: {
    type: String,
    default: "Sign In to continue",
  },
  description: {
    type: String,
    default: "Please Sign In or Sign Up to continue generate your Lesson Plan.",
  },
  redirectPath: {
    type: String,
    default: null,
  },
  limitResourceLeft: {
    type: Number,
    default: 0,
  },
});

const emit = defineEmits(["update:modelValue", "close", "user-authenticated"]);
const store = useStore();
const showAuthModal = ref(false);
const authView = ref("login");

const isVisible = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

// Watch for changes in the Vuex store user data
watch(
  () => store.state.user,
  (newUserData) => {
    if (newUserData) {
      console.log("ViewLimitModal: User authenticated via store update");
      // Close the modal when user becomes authenticated
      isVisible.value = false;
    }
  },
  { immediate: true }
);

const handleClose = () => {
  emit("close");
};

const navigateToLogin = () => {
  // Show AuthModal with login view instead of redirecting
  showAuthModal.value = true;
  isVisible.value = false;
  authView.value = "login";
};

const navigateToSignup = () => {
  // Show AuthModal with register view instead of redirecting
  showAuthModal.value = true;
  isVisible.value = false;
  authView.value = "register";
};

const handleLoginSuccess = (data) => {
  console.log("Login successful from ViewLimitModal:", data);
  showAuthModal.value = false;
  // The store watcher will automatically close the ViewLimitModal
  // Emit a custom event to notify parent that user is now authenticated
  emit("user-authenticated", data);
};

const handleCloseAuthModal = () => {
  showAuthModal.value = false;
};
</script>

<style scoped>
.login-required-modal :deep(.v-card) {
  border-radius: 20px;
}

.modal-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 20px 0;
  overflow: hidden;
}

.illustration-container {
  margin-bottom: 10px;
  max-height: 50vh;
}

.illustration {
  width: 300px;
  height: auto;
  max-width: 100%;
}

.main-title {
  font-size: 2rem;
  font-weight: 600;
  color: var(--primary-blue);
  text-align: center;
  margin: 0;
  line-height: 1.2;
}

.description {
  font-size: 1rem;
  font-weight: 400;
  color: #3b4648;
  text-align: center;
  margin: 0;
  line-height: 1.4;
}

.button-container {
  display: flex;
  flex-direction: column;
  margin-top: 10px;
  gap: 5px;
}

.action-btn {
  min-width: 120px;
  height: 48px;
  border-radius: 16px;
  font-size: var(--fs-medium);
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

.login-btn {
  background-color: var(--primary-blue) !important;
  color: #fff !important;
}

.signup-btn {
  border: none !important;
  background-color: #fff !important;
  color: var(--primary-pink) !important;
  text-decoration: underline !important;
}

.remaining-count {
  color: var(--primary-pink) !important;
}

/* Responsive design */
@media (max-width: 600px) {
  .main-title {
    font-size: var(--fs-xxl);
  }

  .description {
    font-size: var(--fs-medium);
  }

  .button-container {
    flex-direction: column;
    width: 100%;
    gap: 12px;
  }

  .action-btn {
    width: 100%;
  }

  .illustration {
    width: 150px;
  }
}
</style>
